import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import 'leaflet.heat';
import { GeoPoint } from '../../../models/analytics.model';

@Component({
    selector: 'app-analytics-heatmap',
    templateUrl: './analytics-heatmap.component.html',
    styleUrls: ['./analytics-heatmap.component.scss'],
})
export class AnalyticsHeatmapComponent implements OnChanges, OnDestroy {
    @Input() points: GeoPoint[] = [];
    @Input() categories: { id: string; name: string; color: string }[] = [];

    private map: L.Map;
    private heatLayer: any;
    private mapReady = false;
    private defaultCenter: L.LatLngExpression = [45.5017, -73.5673];
    private defaultZoom = 13;

    selectedCategoryId: string | null = null;

    get filteredPoints(): GeoPoint[] {
        if (!this.selectedCategoryId) {
            return this.points;
        }
        return this.points.filter((p) => p.categoryId === this.selectedCategoryId);
    }

    get categoryTabs(): { id: string | null; name: string }[] {
        const allTab = { id: null, name: 'Tous' };
        const catTabs = this.categories.map((c) => ({ id: c.id, name: c.name }));
        return [allTab, ...catTabs];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.points && this.points?.length > 0 && !this.mapReady) {
            // First time we have data — initialize the map
            setTimeout(() => this.initMap(), 100);
        } else if (this.mapReady && (changes.points || changes.categories)) {
            this.updateHeatmap();
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    onCategoryFilter(categoryId: string | null): void {
        this.selectedCategoryId = categoryId;
        this.updateHeatmap();
    }

    private initMap(): void {
        const container = document.getElementById('analytics-heatmap');
        if (!container || container.clientHeight === 0) {
            setTimeout(() => this.initMap(), 200);
            return;
        }

        if (this.map) {
            this.map.remove();
        }

        this.map = L.map('analytics-heatmap', {
            zoomControl: false,
        }).setView(this.defaultCenter, this.defaultZoom);

        L.control.zoom({ position: 'topright' }).addTo(this.map);

        L.tileLayer(MAP_TILE_URL, {
            maxZoom: 18,
            minZoom: 3,
            attribution: MAP_TILE_ATTRIBUTION,
        }).addTo(this.map);

        this.mapReady = true;
        this.updateHeatmap();
    }

    private updateHeatmap(): void {
        if (!this.map) return;

        if (this.heatLayer) {
            this.map.removeLayer(this.heatLayer);
            this.heatLayer = null;
        }

        const pts = this.filteredPoints;
        if (pts.length === 0) return;

        const heatData: L.HeatLatLngTuple[] = pts.map((p) => [p.lat, p.lng, 1]);

        this.heatLayer = (L as any)
            .heatLayer(heatData, {
                radius: 25,
                blur: 15,
                maxZoom: 17,
                max: 1,
                gradient: { 0.4: '#06b6d4', 0.65: '#f59e0b', 1: '#ef4444' },
            })
            .addTo(this.map);

        const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng] as L.LatLngTuple));
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
}
